import type * as BrazeSdk from "@braze/web-sdk";

import { BrazeCardType, BrazeContentCard, mapCard } from "./braze.types";

const API_KEY = process.env.REACT_APP_BRAZE_API_KEY;
const BASE_URL = process.env.REACT_APP_BRAZE_SDK_ENDPOINT;

let braze: typeof BrazeSdk | null = null;
let initPromise: Promise<boolean> | null = null;
let sessionStarted = false;
let appliedExternalId: string | null = null;

export const isBrazeConfigured = (): boolean => Boolean(API_KEY && BASE_URL);

const getCardType = (card: BrazeSdk.Card): BrazeCardType => {
  if (!braze) {
    return "classic";
  }
  if (card instanceof braze.ControlCard || card.isControl) {
    return "control";
  }
  if (card instanceof braze.CaptionedImage) {
    return "captioned";
  }
  if (card instanceof braze.ImageOnly) {
    return "imageOnly";
  }

  return "classic";
};

const mapCards = (cards: BrazeSdk.Card[]): BrazeContentCard[] =>
  cards.map((card) => mapCard(card, getCardType(card)));

const initBraze = (): Promise<boolean> => {
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    if (!isBrazeConfigured() || typeof window === "undefined") {
      return false;
    }

    try {
      const sdk = await import("@braze/web-sdk");
      const initialized = sdk.initialize(API_KEY as string, {
        baseUrl: BASE_URL as string,
        enableLogging: false,
      });

      if (!initialized) {
        // Reset so a later call can retry instead of caching this failure.
        initPromise = null;

        return false;
      }
      braze = sdk;

      return true;
    } catch (error) {
      console.error("Braze: failed to initialize", error);
      // The dynamic `@braze/web-sdk` chunk is a common ad-blocker/flaky-network
      // target; reset so the next call can retry rather than caching `false`.
      initPromise = null;

      return false;
    }
  })();

  return initPromise;
};

export const startBraze = async (
  onCards: (cards: BrazeContentCard[]) => void,
  externalId: string | null,
): Promise<boolean> => {
  const ready = await initBraze();

  if (!ready || !braze) {
    return false;
  }
  if (!sessionStarted) {
    sessionStarted = true;
    // Subscribe before `openSession` so Content Cards refresh on session start.
    braze.subscribeToContentCardsUpdates((updates) => onCards(mapCards(updates.cards)));
    // Identify before `openSession` (per Braze guidance) so the first session
    // and Content Cards fetch are attributed to the known user, not anonymous.
    if (externalId) {
      appliedExternalId = externalId;
      braze.changeUser(externalId);
    }
    braze.openSession();
  }

  return true;
};

export const identifyBrazeUser = (externalId: string): void => {
  if (!braze || appliedExternalId === externalId) {
    return;
  }
  appliedExternalId = externalId;
  braze.changeUser(externalId);
  braze.requestContentCardsRefresh();
};

// Clear the applied-identity guard on logout so a returning user (even the
// same id) is re-identified and their cards refreshed on the next login.
// We intentionally do NOT call `changeUser` here (per Braze guidance).
export const resetBrazeUser = (): void => {
  appliedExternalId = null;
};

export const refreshContentCards = (): void => braze?.requestContentCardsRefresh();

// Clears all locally stored SDK data on logout so the previous user's cached
// profile/cards do not leak into the next session. On web this only resets the
// user to a new anonymous device (it does NOT disable the SDK), so a returning
// user can still start a fresh session on their next login.
export const wipeBrazeData = (): void => braze?.wipeData();

export const getCachedCards = (): BrazeContentCard[] => {
  const cached = braze?.getCachedContentCards();

  return cached ? mapCards(cached.cards) : [];
};

export const logCardImpression = (card: BrazeContentCard): void => {
  braze?.logContentCardImpressions([card.raw]);
};

export const logCardImpressions = (cards: BrazeContentCard[]): void => {
  if (cards.length === 0) {
    return;
  }
  braze?.logContentCardImpressions(cards.map((card) => card.raw));
};

export const logCardClick = (card: BrazeContentCard): void => {
  braze?.logContentCardClick(card.raw);
};
